import {Link, useRouteLoaderData} from '@remix-run/react'
import {useEffect, useRef} from 'react'
import clsx from 'clsx'

import {getTranslation} from '~/localization/translation'
import type {RootLoaderResponse} from '~/types'
import {getDomainConfig} from '~/domains'
import AffiliationNotice from '../commons/affiliation-notice'
import DwsPartnerBanner from './dws-partner-banner'
import CustomerService from './customer-service'
import CompanyInfo from './company-info'

const ignoredMenuItemsIdByLocale = {
  us: [],
  ca: [],
  uk: [],
  in: [],
  au: [],
  de: ['more', 'contact', 'faq', 'disclaimer'],
  it: ['more', 'contact', 'faq', 'disclaimer'],
  fr: ['more', 'contact', 'faq', 'disclaimer'],
  es: ['more', 'contact', 'faq', 'disclaimer'],
} as Record<string, string[]>

const ignoredMenuItemsIdByDomain: Record<string, string[]> = {
  comparermieux: ['shipping-tools', 'more'],
}

const Footer = () => {
  const {locale, language, featureFlags, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles: stylesBydomain, domainName, logo} = getDomainConfig(domain, locale)
  const showDwsPartnerBanner = !(domainName in ignoredMenuItemsIdByDomain)
  const showCcpaAndSharingPolicy = featureFlags?.showCcpaAndSharingPolicy
  const footerRef = useRef<HTMLElement | null>(null)

  const ignoredMenuItems = [
    ...(ignoredMenuItemsIdByLocale[locale.toLowerCase()] || []),
    ...(ignoredMenuItemsIdByDomain[domainName] || []),
    ...(showCcpaAndSharingPolicy ? [] : ['sharing', 'ccpa']),
  ]

  useEffect(() => {
    const shouldLoadBadge =
      process.env.NODE_ENV === 'production' &&
      locale?.toLowerCase() === 'us' &&
      domainName?.toLowerCase() === 'bestreviews'
    if (!shouldLoadBadge) return

    const BADGE_SCRIPT_SELECTOR = 'script[data-rs-badge="1"]'
    let isLoaded = false

    const injectBadgeScript = () => {
      if (isLoaded || document.querySelector(BADGE_SCRIPT_SELECTOR)) return
      isLoaded = true

      const badgeScript = document.createElement('script')
      badgeScript.src = 'https://reliablesite.org/badge.js'
      badgeScript.async = false
      badgeScript.setAttribute('data-rs-badge', '1')
      badgeScript.setAttribute('badg_pos', 'right-middle')
      badgeScript.setAttribute('typebadge', 'easymiddle')
      badgeScript.setAttribute('postion', 'right')
      badgeScript.setAttribute('site_id', '1940')
      badgeScript.setAttribute('mainclassname', 'main_right-middle')
      badgeScript.setAttribute('stylemain', 'transform: rotate(90deg);margin-right: -75px;')
      badgeScript.setAttribute('badgemain', 'border-radius: 0 0 20px 20px;')
      badgeScript.setAttribute('fontbold_ver', '500')
      badgeScript.setAttribute('fontcolor_ver', '#A81620')
      badgeScript.setAttribute('rotaters', 'transform: rotate(-90deg);')
      badgeScript.setAttribute('badgers', 'https://reliablesite.org/badges/rslogo_red.png')
      badgeScript.setAttribute('easymiddle_language', 'english')
      badgeScript.setAttribute('scroll', '')
      badgeScript.setAttribute('scrollpixel', '')

      document.body.appendChild(badgeScript)
    }

    const fallbackTimeout = setTimeout(injectBadgeScript, 2500)

    const target = footerRef.current
    if (!target) {
      return () => clearTimeout(fallbackTimeout)
    }

    if (!('IntersectionObserver' in window)) {
      return () => clearTimeout(fallbackTimeout)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        injectBadgeScript()
        observer.disconnect()
      },
      {rootMargin: '400px 0px'},
    )

    observer.observe(target)
    return () => {
      clearTimeout(fallbackTimeout)
      observer.disconnect()
    }
  }, [locale, domainName])

  return (
    <footer ref={footerRef} className="h-100 relative mt-auto flex w-screen flex-col justify-center">
      <AffiliationNotice />
      <div
        className={clsx(
          stylesBydomain.COMMONS_FOOTER_BG,
          stylesBydomain.COMMONS_FOOTER_TEXT,
          'commons_footer_bg commons_footer_text',
        )}
      >
        <div
          className={clsx(
            'utils_main_container mx-auto flex flex-col justify-start py-16 text-white desktop:flex-row',
            stylesBydomain.UTILS_MAIN_CONTAINER,
          )}
        >
          <div className="mb-12 mt-1 flex flex-col gap-4 desktop:mb-0 desktop:min-w-fit desktop:items-start tablet:flex-row tablet:items-start tablet:justify-between">
            {logo}

            <CompanyInfo className="flex flex-col gap-1 text-sm mobile:w-1/2" />
            <CustomerService className="flex flex-col gap-1 text-sm tablet:hidden" />
          </div>
          <section
            className={clsx('flex w-full justify-between', stylesBydomain.COMMONS_FOOTER_TEXT, 'commons_footer_text')}
          >
            {translations.Footer.Navigation.filter((i) => !ignoredMenuItems.includes(i.id)).map((item, menuIndex) => (
              <div
                key={`${item}-${menuIndex}`}
                className="flex w-full flex-col items-center tablet:items-start tablet:pr-5"
              >
                <p className="mb-5 text-sm font-medium uppercase">{item.title}</p>
                <ul className={clsx('text-sm', item?.id === 'company' && showCcpaAndSharingPolicy && 'desktop:pl-14')}>
                  {item.menuItems
                    .filter((i) => !ignoredMenuItems.includes(i.id))
                    .map((menuItem, menuItemIndex) => (
                      <li
                        key={`${menuItem}-${menuItemIndex}`}
                        className={clsx('mb-4 list-none', showCcpaAndSharingPolicy && 'desktop:max-w-[150px]')}
                      >
                        <Link
                          reloadDocument
                          prefetch="intent"
                          to={menuItem.href}
                          aria-label={`explore ${menuItem.title} menu`}
                        >
                          {menuItem.title}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </section>
          <CustomerService className="mt-2 hidden flex-col gap-1 text-sm tablet:flex" />
        </div>
      </div>
      {showDwsPartnerBanner && <DwsPartnerBanner />}
    </footer>
  )
}

export default Footer
